import { formatHours, formatIntacctDate, useUserDateFormat } from '@sit/client-shared';
import DebouncedTextbox from '@web/components/Shared/Textbox/DebouncedTextbox';
import StatusPill from '@web/components/StatusPill';
import { useGetPillInfo } from '@web/helpers/pills';
import Divider from 'carbon-react/lib/components/divider';
import Preview from 'carbon-react/lib/components/preview';
import Typography from 'carbon-react/lib/components/typography';
import React from 'react';
import styled, { css } from 'styled-components';

type StickPositions = 'top' | 'left' | 'right' | 'bottom';

const WIDE_CARD_MAX_WIDTH = 300;
const WIDE_CARD_MIN_WIDTH = 200;
const DEFAULT_WIDE_CARD_SHRINK_START = 1570;

const RootStickAt = css<{ stickAt?: StickPositions }>`
  position: sticky;
  ${({ stickAt = 'top' }) => `${stickAt}: 0`};
`;

const Root = styled.div<{ stickAt?: StickPositions; wide?: boolean; wideCardShrinkStart?: number }>`
  flex-basis: ${({ wide }) => (wide ? `${WIDE_CARD_MAX_WIDTH}px` : '130px')};
  flex-shrink: 0;
  height: 96px;
  display: flex;

  ${({ wide, wideCardShrinkStart = DEFAULT_WIDE_CARD_SHRINK_START }) =>
    wide
      ? css`
          min-width: ${WIDE_CARD_MIN_WIDTH}px;

          @media (max-width: ${wideCardShrinkStart}px) {
            flex-basis: clamp(
              ${WIDE_CARD_MIN_WIDTH}px,
              calc(100vw - ${wideCardShrinkStart - WIDE_CARD_MAX_WIDTH}px),
              ${WIDE_CARD_MAX_WIDTH}px
            );
          }
        `
      : css`
          @media (min-width: 1670px) {
            flex-basis: 145px;
          }

          @media (min-width: 1770px) {
            flex-basis: 160px;
          }

          @media (min-width: 1880px) {
            flex-basis: 175px;
          }

          @media (min-width: 2020px) {
            flex-basis: 195px;
          }
        `}

  ${({ stickAt }) => stickAt && RootStickAt}
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0 ${({ theme }) => theme.space[2]};
  text-align: center;
  gap: ${({ theme }) => theme.space[1]};
  background-color: ${({ theme }) => theme.colors.white};
  @media (max-width: 1580px) {
    padding: 0 ${({ theme }) => theme.space[1]};
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;
const Value = styled.div`
  width: 100%;
  & > h4 {
    line-height: 26px;
    width: 100%;
    text-align: center;
  }
`;

const BigTextValue = styled(Value)`
  color: ${({ theme }) => theme.palette.black};
  font-size: 24px;
  font-weight: 700;
`;

interface StatusCardBaseCommonProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  stickAt?: StickPositions;
  wide?: boolean;
  wideCardShrinkStart?: number;
  withDivider?: boolean;
  'data-testid'?: string;
}

type StatusCardBasePropsCommonWithValue<Value extends React.ReactNode> = StatusCardBaseCommonProps & { value: Value };
type StatusCardBasePropsCommonWithValueComponent = StatusCardBaseCommonProps & {
  valueComponent: React.ReactNode;
};

type StatusCardBaseProps<Value extends React.ReactNode> =
  | StatusCardBasePropsCommonWithValue<Value>
  | StatusCardBasePropsCommonWithValueComponent;

const StatusCardBase = ({ label, stickAt, wide, wideCardShrinkStart, withDivider, ...props }: StatusCardBaseProps<string>) => {
  const haveValueComponent = 'valueComponent' in props;
  return (
    <Root stickAt={stickAt} wide={wide} wideCardShrinkStart={wideCardShrinkStart}>
      {withDivider && <Divider type="vertical" variant="typical" py={2} px={0} />}
      <Item {...props}>
        <Label>{label}</Label>
        <Value data-testid="stat-value">
          {haveValueComponent ? (
            props.valueComponent
          ) : (
            <Typography variant="h4" truncate mt={0} mb={0}>
              {props.value}
            </Typography>
          )}
        </Value>
      </Item>
    </Root>
  );
};

interface StatusCardPropsBase<Value, Variant extends string> extends StatusCardBaseCommonProps {
  type: Variant;
  value: Value;
}

interface StatusCardPropsComponent extends StatusCardBaseCommonProps {
  type: 'component';
  valueComponent: React.ReactNode;
}

export type StatusCardProps =
  | StatusCardPropsBase<Date, 'date'>
  | StatusCardPropsBase<number, 'hours'>
  | StatusCardPropsBase<string, 'pill-status'>
  | StatusCardPropsBase<void, 'preview'>
  | StatusCardPropsBase<string, 'text'>
  | StatusCardPropsBase<{ value: string; isLoading?: boolean; disabled?: boolean; onChange?: (value: string) => void }, 'description'>
  | StatusCardPropsComponent;

const StatusCard = (props: StatusCardProps) => {
  const { type } = props;
  const pillInfo = useGetPillInfo(type === 'pill-status' ? props.value : undefined);
  const dateFormat = useUserDateFormat();

  switch (type) {
    case 'date':
      return <StatusCardBase {...props} value={formatIntacctDate(props.value, dateFormat)} />;
    case 'hours':
      return <StatusCardBase {...props} valueComponent={<BigTextValue>{formatHours(props.value)}</BigTextValue>} />;
    case 'pill-status': {
      return <StatusCardBase valueComponent={<StatusPill text={pillInfo.pillText} type={pillInfo.pillType} />} {...props} />;
    }
    case 'preview':
      return <StatusCardBase {...props} label={props.label || <Preview loading width="70px" />} valueComponent={<Preview loading />} />;
    case 'text':
      return <StatusCardBase {...props} value={props.value} />;
    case 'description':
      return (
        <StatusCardBase
          {...props}
          wide
          label={props.label}
          valueComponent={
            props.value.isLoading ? (
              <Preview height="40px" width="100%" />
            ) : (
              <DebouncedTextbox
                value={props.value.value}
                onValueChangeDebounced={props.value.onChange}
                disabled={props.value.disabled}
                data-cy="timesheet-description"
              />
            )
          }
        />
      );
    case 'component':
      return <StatusCardBase {...props} wide label={props.label} valueComponent={props.valueComponent} />;
    default:
      return null;
  }
};
export default StatusCard;
